import { useState } from "react";
import { useAuth, API, api } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon, AIDisclaimer } from "@/components/kit";
import { toast } from "sonner";

const CHANNELS = ["LinkedIn", "Instagram", "WhatsApp", "X (Twitter)", "Facebook"];
const TYPES = [
  { t: "Social post", icon: "Megaphone" }, { t: "Festival creative", icon: "PartyPopper" },
  { t: "SIP education", icon: "GraduationCap" }, { t: "Market update", icon: "TrendingUp" },
  { t: "Newsletter", icon: "Mail" }, { t: "Reel script", icon: "Video" },
];
const KIT_SECTIONS = [
  { key: "whatsapp", label: "WhatsApp message", icon: "MessageCircle" },
  { key: "email", label: "Email", icon: "Mail" },
  { key: "linkedin", label: "LinkedIn post", icon: "Linkedin" },
  { key: "reel_script", label: "Reel / video script", icon: "Video" },
  { key: "calling_script", label: "Client calling script", icon: "Phone" },
];

export default function Marketing() {
  const { refreshUser } = useAuth();
  const [tab, setTab] = useState("factory");

  // Campaign Factory
  const [goal, setGoal] = useState("Acquire 25 new SIP clients this month");
  const [kit, setKit] = useState(null);
  const [kBusy, setKBusy] = useState(false);
  const [cImgs, setCImgs] = useState([]);
  const [cFrames, setCFrames] = useState([]);
  const [cImgBusy, setCImgBusy] = useState(false);
  const [cSbBusy, setCSbBusy] = useState(false);
  const genVisual = async (path, setter, setB) => {
    setB(true);
    try {
      const { data } = await api.post(path, { topic: goal });
      setter(data.images || data.frames || []); refreshUser();
      toast.success("Visuals ready");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Insufficient wallet balance. Please top up.");
      else toast.error("Image generation failed. Try again.");
    } finally { setB(false); }
  };

  // Creative Studio
  const [channel, setChannel] = useState("LinkedIn");
  const [type, setType] = useState("Social post");
  const [topic, setTopic] = useState("Why SIPs beat timing the market");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);

  // Compliance
  const [content, setContent] = useState("Invest in our top fund and get guaranteed 15% returns every year! Best scheme in India, no risk at all.");
  const [review, setReview] = useState("");
  const [cBusy, setCBusy] = useState(false);

  const buildCampaign = async () => {
    if (!goal.trim()) return toast.error("Enter your goal");
    setKBusy(true); setKit(null); setCImgs([]); setCFrames([]);
    try {
      const { data } = await api.post("/marketing/campaign", { goal });
      setKit(data.kit); refreshUser(); toast.success("Campaign kit ready");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Insufficient wallet balance. Please top up.");
      else toast.error("Generation failed. Try again.");
    } finally { setKBusy(false); }
  };
  const copy = (t) => { navigator.clipboard.writeText(typeof t === "string" ? t : JSON.stringify(t)); toast.success("Copied"); };

  const stream = async (mode, prompt, setter, setB) => {
    setB(true); setter("");
    try {
      const doFetch = () => fetch(`${API}/ai/chat`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: prompt, mode }),
      });
      let res = await doFetch();
      if (res.status === 401) { try { await api.post("/auth/refresh"); res = await doFetch(); } catch (e) { console.error("Auth refresh failed", e); } }
      if (res.status === 402) { toast.error("Insufficient wallet balance."); setB(false); return; }
      if (!res.ok) { toast.error("AI error"); setB(false); return; }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { done, value } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true }); const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) { if (!p.startsWith("data: ")) continue; const d = JSON.parse(p.slice(6)); if (d.delta) setter((s) => s + d.delta); }
      }
      refreshUser();
    } catch { toast.error("AI error"); } finally { setB(false); }
  };
  const generate = () => stream("content", `Create a ${type} for ${channel} on the topic: "${topic}". Keep it SEBI/AMFI compliant with a risk disclaimer.`, setOut, setBusy);
  const checkCompliance = () => stream("compliance", `Review this MFD marketing content for SEBI/AMFI compliance:\n\n"${content}"`, setReview, setCBusy);

  const TABS = [["factory", "Campaign Factory"], ["studio", "Creative Studio"], ["compliance", "Compliance Copilot"]];

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="MFDX Marketing Factory" subtitle="Turn a goal into a full campaign — then create and compliance-check every asset." icon="Megaphone" color="#F97316" />

      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`mkt-tab-${k}`}
            className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap ${tab === k ? "grad-blue-violet text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "factory" && (
        <div>
          <Card className="mb-6">
            <label className="text-sm font-medium">What's your goal this month?</label>
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <input value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="campaign-goal-input"
                className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-blue-400" placeholder="e.g. Acquire 25 new SIP clients this month" />
              <Btn onClick={buildCampaign} disabled={kBusy} data-testid="campaign-build-btn"><Icon name="Sparkles" size={16} /> {kBusy ? "Building…" : "Build campaign"}</Btn>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {["Acquire 25 new SIP clients this month", "Reactivate 20 dormant clients", "Grow my SIP book by ₹5 lakh/month", "Get 15 referrals from happy clients"].map((g) => (
                <button key={g} onClick={() => setGoal(g)} className="text-xs bg-orange-50 text-orange-700 rounded-full px-3 py-1.5 hover:bg-orange-100">{g}</button>
              ))}
            </div>
            <span className="text-xs text-gray-400 mt-3 inline-block">Cost: <b className="text-gray-700">₹15</b> per campaign kit</span>
          </Card>

          {kBusy && <div className="text-center text-gray-400 py-10"><Icon name="Loader" size={28} className="mx-auto mb-2 animate-spin" />Designing your campaign strategy, messages & calendar…</div>}

          {kit && (
            <div className="space-y-4" data-testid="campaign-kit">
              {kit.strategy && (
                <div className="rounded-[24px] bg-[#0A0A0A] text-white p-6 relative overflow-hidden">
                  <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full grad-blue-violet opacity-25 blur-3xl" />
                  <div className="flex items-center gap-2 mb-2 relative"><Icon name="Target" size={18} className="text-blue-300" /><span className="font-head font-semibold text-lg">Campaign strategy</span></div>
                  <p className="text-gray-200 text-sm leading-relaxed relative whitespace-pre-wrap">{kit.strategy}</p>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                {KIT_SECTIONS.filter((s) => kit[s.key]).map((s) => (
                  <Card key={s.key}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 rounded-xl bg-orange-50 grid place-items-center"><Icon name={s.icon} size={18} className="text-orange-600" /></div>
                      <h3 className="font-head font-semibold">{s.label}</h3>
                      <Btn variant="soft" className="ml-auto !px-3 !py-1.5" onClick={() => copy(kit[s.key])} data-testid={`campaign-copy-${s.key}`}><Icon name="Copy" size={13} /> Copy</Btn>
                    </div>
                    <div className="text-sm whitespace-pre-wrap text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-4 max-h-64 overflow-y-auto">{kit[s.key]}</div>
                  </Card>
                ))}
              </div>
              {Array.isArray(kit.followups) && kit.followups.length > 0 && (
                <Card>
                  <h3 className="font-head font-semibold mb-3 flex items-center gap-2"><Icon name="Send" size={16} className="text-violet-600" /> Follow-up messages</h3>
                  <div className="space-y-2">{kit.followups.map((f, i) => (
                    <div key={`fu-${i}-${(f || "").slice(0, 16)}`} className="flex gap-2 text-sm text-gray-700 bg-violet-50 rounded-xl px-3 py-2"><span className="font-bold text-violet-500">{i + 1}.</span>{f}</div>
                  ))}</div>
                </Card>
              )}
              {Array.isArray(kit.calendar) && kit.calendar.length > 0 && (
                <Card>
                  <h3 className="font-head font-semibold mb-3 flex items-center gap-2"><Icon name="CalendarDays" size={16} className="text-blue-600" /> Campaign calendar</h3>
                  <div className="space-y-2">{kit.calendar.map((c, i) => (
                    <div key={`cal-${i}-${c.day || ""}`} className="flex gap-3 text-sm border-b border-gray-100 pb-2 last:border-0">
                      <span className="font-semibold text-blue-600 w-24 shrink-0">{c.day}</span>
                      <span className="text-gray-700">{c.action}</span>
                    </div>
                  ))}</div>
                </Card>
              )}
              <Card>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-orange-50 grid place-items-center"><Icon name="Image" size={18} className="text-orange-600" /></div>
                  <div><h3 className="font-head font-semibold">Campaign visuals</h3><p className="text-xs text-gray-400">AI creatives for this campaign</p></div>
                  <div className="ml-auto flex flex-wrap gap-2">
                    <Btn variant="outline" onClick={() => genVisual("/content/visuals", setCImgs, setCImgBusy)} disabled={cImgBusy} data-testid="camp-gen-images-btn"><Icon name="Sparkles" size={14} /> {cImgBusy ? "Creating…" : "Generate images (₹10)"}</Btn>
                    <Btn variant="outline" onClick={() => genVisual("/content/storyboard", setCFrames, setCSbBusy)} disabled={cSbBusy} data-testid="camp-gen-storyboard-btn"><Icon name="Video" size={14} /> {cSbBusy ? "Creating…" : "Reel storyboard (₹12)"}</Btn>
                  </div>
                </div>
                {(cImgBusy || cSbBusy) && <div className="text-center text-gray-400 py-6"><Icon name="Loader" size={24} className="mx-auto mb-2 animate-spin" />Generating visuals… ~15–30s</div>}
                {cImgs.length > 0 && (
                  <div className="grid sm:grid-cols-2 gap-4 mb-4" data-testid="camp-images-output">
                    {cImgs.map((im) => (
                      <div key={im.kind} className="rounded-xl overflow-hidden border border-gray-100">
                        <img src={im.data_url} alt={im.label} className="w-full object-cover" />
                        <div className="flex items-center gap-2 p-2 bg-gray-50 text-xs"><span className="font-medium">{im.label} · {im.aspect}</span><a href={im.data_url} download={`mfdx-campaign-${im.kind}.png`} className="ml-auto text-blue-600 font-semibold flex items-center gap-1" data-testid={`camp-img-download-${im.kind}`}><Icon name="Download" size={13} /> Download</a></div>
                      </div>
                    ))}
                  </div>
                )}
                {cFrames.length > 0 && (
                  <div>
                    <h4 className="font-head font-semibold text-sm mb-2">Reel storyboard</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="camp-storyboard-output">
                      {cFrames.map((f, i) => (
                        <div key={f.label} className="rounded-xl overflow-hidden border border-gray-100"><img src={f.data_url} alt={f.label} className="w-full object-cover aspect-[9/16]" /><div className="flex items-center gap-2 p-2 bg-gray-50 text-xs"><span className="font-medium">{i + 1}. {f.label}</span><a href={f.data_url} download={`mfdx-campaign-frame-${i + 1}.png`} className="ml-auto text-blue-600 font-semibold"><Icon name="Download" size={13} /></a></div></div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
              <AIDisclaimer />
            </div>
          )}
        </div>
      )}

      {tab === "studio" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Card>
            <h3 className="font-head font-semibold text-lg mb-4">Generate content</h3>
            <label className="text-sm font-medium">Content type</label>
            <div className="grid grid-cols-3 gap-2 mt-2 mb-4">
              {TYPES.map((t) => (
                <button key={t.t} onClick={() => setType(t.t)} className={`rounded-xl p-3 border text-center ${type === t.t ? "border-blue-400 bg-blue-50" : "border-gray-200"}`}>
                  <Icon name={t.icon} size={18} className="mx-auto mb-1" style={{ color: type === t.t ? "#3B82F6" : "#9CA3AF" }} />
                  <div className="text-[11px] font-medium">{t.t}</div>
                </button>
              ))}
            </div>
            <label className="text-sm font-medium">Channel</label>
            <div className="flex flex-wrap gap-2 mt-2 mb-4">
              {CHANNELS.map((c) => <button key={c} onClick={() => setChannel(c)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${channel === c ? "grad-blue-violet text-white border-transparent" : "border-gray-200 text-gray-600"}`}>{c}</button>)}
            </div>
            <label className="text-sm font-medium">Topic</label>
            <textarea rows={2} value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="mkt-topic-input" className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400" />
            <div className="flex items-center gap-3 mt-4">
              <Btn onClick={generate} disabled={busy} data-testid="mkt-generate-btn"><Icon name="Sparkles" size={16} /> {busy ? "Generating…" : "Generate"}</Btn>
              <span className="text-sm text-gray-400">Cost: <b className="text-gray-700">₹2</b></span>
            </div>
          </Card>
          <Card>
            <h3 className="font-head font-semibold text-lg mb-3">Preview</h3>
            {out ? <div className="text-sm whitespace-pre-wrap text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-4 min-h-[240px]">{out}</div>
              : <div className="min-h-[240px] grid place-items-center text-gray-400 text-sm border-2 border-dashed border-gray-100 rounded-xl">Your AI-generated content will appear here</div>}
            {out && <div className="flex gap-2 mt-3"><Btn variant="soft" onClick={() => copy(out)}><Icon name="Copy" size={14} /> Copy</Btn><Btn variant="outline" onClick={() => { setTab("compliance"); setContent(out); }}><Icon name="ShieldCheck" size={14} /> Check compliance</Btn></div>}
            <AIDisclaimer />
          </Card>
        </div>
      )}

      {tab === "compliance" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Card>
            <h3 className="font-head font-semibold text-lg mb-3">Content to review</h3>
            <textarea rows={8} value={content} onChange={(e) => setContent(e.target.value)} data-testid="compliance-input" className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm outline-none focus:border-blue-400" />
            <Btn onClick={checkCompliance} disabled={cBusy} className="mt-3" data-testid="compliance-check-btn"><Icon name="ShieldCheck" size={16} /> {cBusy ? "Analysing…" : "Run Compliance Check"}</Btn>
            <div className="mt-3 text-[11px] text-gray-400 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 flex gap-2"><Icon name="AlertTriangle" size={13} className="text-amber-500 shrink-0" /> AI compliance checking is assistive only and does not replace your responsibility, AMC approval, or professional/legal review.</div>
          </Card>
          <Card>
            <h3 className="font-head font-semibold text-lg mb-3">Compliance report</h3>
            {review ? <div className="text-sm whitespace-pre-wrap text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-4 min-h-[240px]">{review}</div>
              : <div className="min-h-[240px] grid place-items-center text-center text-gray-400 text-sm border-2 border-dashed border-gray-100 rounded-xl p-6">Paste your post, WhatsApp message or creative copy and run the check.<br /><span className="text-xs">SEBI · AMFI · advertising principles · risk disclosures</span></div>}
          </Card>
        </div>
      )}
    </div>
  );
}
